import assignAirport from './assignAirport';
import assignPerson from './assignPerson';
import setReservationEntityData from './setReservationEntityData';
import Entity from '../../schema/Entity';
import FlightReservation from '../../schema/FlightReservation';
import EntityType from '../../schema/innerSchema/EntityType';
import EntityTypeNames from '../../schema/innerSchema/EntityTypeNames';

/**
 * parse data for FlightReservation Entity
 */
export default function setFlightEntityData(
    entity: Entity,
    flightEntity: any,
    isValidDate?: (dateTime: string, isUtcTime: boolean, entity?: Entity) => boolean
): FlightReservation {
    if (entity && flightEntity) {
        let newFlightEntity: FlightReservation = setReservationEntityData(
            entity,
            flightEntity,
            false /* isReservationIdRequired */
        );
        // assign the flight data
        if (isFlightValid(flightEntity, entity, isValidDate)) {
            let reservationFor = flightEntity.reservationFor;
            let airline = reservationFor.provider;
            newFlightEntity = {
                ...newFlightEntity,
                ...{
                    entityName: EntityTypeNames.FlightReservation,
                    entityType: EntityType.FlightReservation,
                    checkinUrl: flightEntity.checkinUrl
                        ? flightEntity.checkinUrl
                        : flightEntity.potentialAction
                        ? flightEntity.potentialAction.target
                        : null,
                    departureTime: reservationFor.departureTime,
                    departureTimeUTC: reservationFor['departureTime/utc'],
                    departureTimeRevised: reservationFor['departureTime/revised'],
                    departureTimeUtcRevised: reservationFor['departureTime/utc/revised'],
                    departureTerminal: reservationFor.departureTerminal,
                    departureTerminalRevised: reservationFor['departureTerminal/revised'],
                    departureGate: reservationFor.departureGate,
                    departureGateRevised: reservationFor['departureGate/revised'],
                    departureTimeZone: reservationFor['departureTime/windowsTimeZoneName'],
                    arrivalTime: reservationFor.arrivalTime,
                    arrivalTimeUTC: reservationFor['arrivalTime/utc'],
                    arrivalTimeRevised: reservationFor['arrivalTime/revised'],
                    arrivalTimeUtcRevised: reservationFor['arrivalTime/utc/revised'],
                    arrivalTerminal: reservationFor.arrivalTerminal,
                    arrivalTerminalRevised: reservationFor['arrivalTerminal/revised'],
                    arrivalGate: reservationFor.arrivalGate,
                    arrivalGateRevised: reservationFor['arrivalGate/revised'],
                    arrivalTimeZone: reservationFor['arrivalTime/windowsTimeZoneName'],
                    flightNumber: reservationFor.flightNumber,
                    flightNumberRevised: reservationFor['flightNumber/revised'],
                    flightStatusRevised: reservationFor['flightStatus/revised'],
                    airline: {
                        name: airline.name,
                        iataCode: airline.iataCode,
                    },
                    passengerNames: flightEntity['underName/passengerNames']?.map(passenger =>
                        assignPerson({}, passenger)
                    ),
                },
            };
            // assign data for departure airport
            newFlightEntity = <FlightReservation>{
                ...newFlightEntity,
                ...{
                    departureAirport: assignAirport(
                        newFlightEntity,
                        reservationFor.departureAirport
                    ),
                },
            };

            // assign data for arrival airport
            newFlightEntity = <FlightReservation>{
                ...newFlightEntity,
                ...{
                    arrivalAirport: assignAirport(newFlightEntity, reservationFor.arrivalAirport),
                },
            };

            return newFlightEntity as FlightReservation;
        }
    }
    return null;
}

export function isFlightValid(
    flightEntity: any,
    entity: Entity,
    isValidDate?: (dateTime: string, isUtcTime: boolean, entity?: Entity) => boolean
): boolean {
    if (flightEntity?.reservationFor?.provider) {
        let reservationFor = flightEntity.reservationFor;
        let provider = flightEntity.reservationFor.provider;

        const isDateValidUsingCallback: boolean = isValidDate
            ? isValidDate(reservationFor.arrivalTime, false /* isUtcTime*/, entity) &&
              isValidDate(reservationFor.arrivalTimeUTC, true /* isUtcTime*/, entity) &&
              isValidDate(reservationFor.departureTime, false /* isUtcTime*/, entity) &&
              isValidDate(reservationFor.departureTimeUTC, true /* isUtcTime*/, entity)
            : true;

        // validate Airline
        if (
            provider.iataCode &&
            provider.name &&
            // validate Airports
            isAirportValid(reservationFor.arrivalAirport) &&
            isAirportValid(reservationFor.departureAirport) &&
            // validate require flight details
            reservationFor.flightNumber &&
            reservationFor.arrivalTime &&
            reservationFor.departureTime &&
            isDateValidUsingCallback
        ) {
            return true;
        }
    }
    return false;
}

export function isAirportValid(airportBlob: any): boolean {
    if (
        airportBlob?.iataCode &&
        (airportBlob['address/commonCity'] || airportBlob.address?.addressLocality)
    ) {
        return true;
    }
    return false;
}
